import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminSearchUsers, adminSearchOrganizations } from "../../api/api";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "../notifications_page/NotificationTabShared";

const searchInputClass =
  "w-full rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background";

/**
 * Human-readable verification source for admin user search results.
 *
 * @param {Object} row
 * @returns {string}
 */
export function formatUserVerification(row) {
  const status = row.status || "pending";
  if (row.verified_by === "platform") {
    return `Status: ${status} · Verified by platform`;
  }
  if (row.verified_by === "organization") {
    const orgName = row.verified_by_org_name
      ? row.verified_by_org_name
      : row.verified_by_org_id
        ? `organization #${row.verified_by_org_id}`
        : "an organization";
    return `Status: ${status} · Verified by ${orgName}`;
  }
  return `Status: ${status}`;
}

/**
 * Initials for an avatar fallback.
 *
 * @param {string} name
 * @returns {string}
 */
function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/**
 * @typedef {"users"|"organizations"} AdminDirectoryMode
 */

/**
 * @param {Object} props
 * @param {number} props.adminId
 * @param {AdminDirectoryMode} props.mode
 * @param {string} props.title
 * @param {string} props.description
 * @returns {JSX.Element}
 */
function AdminDirectoryTab({ adminId, mode, title, description }) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 320);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    async function runSearch() {
      if (!adminId) return;
      setLoading(true);
      setError("");
      const api =
        mode === "users"
          ? adminSearchUsers(adminId, debouncedQuery)
          : adminSearchOrganizations(adminId, debouncedQuery);
      const result = await api;
      if (cancelled) return;
      if (result.error) {
        setError(result.error);
        setItems([]);
      } else {
        setItems(Array.isArray(result.data) ? result.data : []);
      }
      setLoading(false);
    }
    runSearch();
    return () => {
      cancelled = true;
    };
  }, [adminId, debouncedQuery, mode]);

  const emptySubtitle =
    debouncedQuery.trim() === ""
      ? "Enter a search term to find accounts."
      : "No accounts matched your search.";

  return (
    <div>
      <NotificationTabHeader title={title} description={description} />

      <label className="mb-3 block text-sm font-medium text-text-primary" htmlFor={`admin-${mode}-search`}>
        Search
      </label>
      <input
        id={`admin-${mode}-search`}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={
          mode === "users"
            ? "Username, email, or name"
            : "Organization name, username, or email"
        }
        autoComplete="off"
        className={`${searchInputClass} mb-4 max-w-md`}
      />

      {error ? <NotificationErrorPanel message={error} /> : null}

      {loading ? (
        <NotificationEmptyState title="Searching…" className="border-solid bg-ui-card" />
      ) : null}

      {!loading && items.length === 0 ? (
        <NotificationEmptyState
          title={debouncedQuery.trim() === "" ? "No search yet" : "No results"}
          subtitle={emptySubtitle}
          className="border-solid bg-ui-card"
        />
      ) : null}

      {!loading && items.length > 0 ? (
        <ul className="space-y-3" aria-label={`${title} results`}>
          {items.map((row) => {
            const profilePath =
              mode === "users"
                ? `/platform-admin/users/${row.id}`
                : `/platform-admin/organizations/${row.id}`;
            const label =
              mode === "users"
                ? `${row.first_name} ${row.last_name}`.trim() || row.username
                : row.name || row.username;
            return (
              <li key={row.id}>
                <Link
                  to={profilePath}
                  className="block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background"
                  aria-label={`View profile for ${label}`}
                >
                  <NotificationRequestCard className="cursor-pointer">
                    <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                      <Avatar className="h-16 w-16 border-2 border-ui-border">
                        <AvatarImage
                          src={row.pfp_url || defaultPfpUrl}
                          alt=""
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = defaultPfpUrl;
                          }}
                        />
                        <AvatarFallback className="text-text-secondary">
                          {initials(label)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        {mode === "users" ? (
                          <>
                            <h3 className="truncate text-lg font-semibold text-text-primary">
                              {row.first_name} {row.last_name}
                            </h3>
                            <p className="truncate text-sm text-text-secondary">@{row.username}</p>
                            <p className="truncate text-sm text-text-muted">{row.email}</p>
                            <p className="truncate text-xs uppercase tracking-wide text-text-muted">
                              {formatUserVerification(row)}
                            </p>
                          </>
                        ) : (
                          <>
                            <h3 className="truncate text-lg font-semibold text-text-primary">{row.name}</h3>
                            <p className="truncate text-sm text-text-secondary">@{row.username}</p>
                            <p className="truncate text-sm text-text-muted">{row.email}</p>
                            <p className="truncate text-xs uppercase tracking-wide text-text-muted">
                              Status: {row.status}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </NotificationRequestCard>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * @param {{adminId: number}} props
 * @returns {JSX.Element}
 */
export function AdminUsersTab({ adminId }) {
  return (
    <AdminDirectoryTab
      adminId={adminId}
      mode="users"
      title="Users"
      description="Search individual accounts by username, email, or name. Click a result to open the profile; accounts can only be deleted from that view."
    />
  );
}

/**
 * @param {{adminId: number}} props
 * @returns {JSX.Element}
 */
export function AdminOrganizationsTab({ adminId }) {
  return (
    <AdminDirectoryTab
      adminId={adminId}
      mode="organizations"
      title="Organizations"
      description="Search organizations by name, username, or email. Click a result to open the profile; accounts can only be deleted from that view."
    />
  );
}
